import React, { useState } from 'react';
import moment from 'moment-timezone';
import { ISOReportsTableContext, IISOReportTableContext } from './ISOReportsTableContext';
import { useAsyncData } from '@shared/react';
import { useErrorNotify } from '@features/useErrorNotify';
import { usePagination } from '@features/Pagination';
import { combineFilterWithOp, joinFiltersWithComma } from '@shared/utils/query';
import { getCurrDay, getPrevDay } from '../../api';
import { useDrawer } from '@features/Modal';
import { ISOReportsDrawer } from '../../components';
import { IServiceTransferReportDto } from '@shared/api';
import { IsoReportType } from './';

export const ISOReportsTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [date, setDate] = useState<string | null>(null);
    const [isoReportsType, setISOReportsType] = useState<IsoReportType>('current');

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination();

    const {
        data: isoReports,
        loading: isoReportsLoading,
        error: isoReportsError,
        handleRefresh: isoReportsRefresh,
    } = useAsyncData(
        async () => {
            const dateFilter = {
                from: {
                    keyOverride: 'fileDate',
                    value: date?.length && moment(date).startOf('day').format(),
                    operator: '>=',
                },
                to: {
                    keyOverride: 'fileDate',
                    value: date?.length && moment(date).endOf('day').format(),
                    operator: '<=',
                },
            };
            const query = {
                page: page,
                pageSize,
                filter: joinFiltersWithComma(combineFilterWithOp(dateFilter)),
            };
            const { items, totalResults } =
                isoReportsType === 'current' ? await getCurrDay(query) : await getPrevDay(query);
            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [page, pageSize, date, isoReportsType],
    );

    useErrorNotify(isoReportsError, 'Payment Requests');

    const { open: openISOReportDrawer } = useDrawer(ISOReportsDrawer);

    const onISOReportViewClick = (isoReport: IServiceTransferReportDto) => {
        openISOReportDrawer({ isoReport, type: isoReportsType });
    };

    const value: IISOReportTableContext = {
        date,
        isoReports,
        isoReportsLoading,
        paginationOptions,
        isoReportsType,
        onISOReportViewClick,
        setDate,
        setISOReportsType,
    };

    return <ISOReportsTableContext.Provider value={value}>{children}</ISOReportsTableContext.Provider>;
};
